import qs from 'qs';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import type { AnyFilter } from '../../shared/organizationPackages/filters';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../components/generic/Pagination/consts';
import { useLocation, useRouter } from '../contexts/RouterContext';
import { flattenFilters, parseQueryParams } from '../helpers/packageInventory';
import type { PackageInventoryQueryParams } from '../helpers/packageInventory';
import type { PackageInventoryURLState } from '../helpers/packageInventory';
import TeamsStore from '../redux/teams';

type FilterPatch = {
  [Key in keyof AnyFilter]?: AnyFilter[Key] | null;
};

const cleanFilters = (filters: FilterPatch): AnyFilter => Object.fromEntries(
  Object.entries(filters).filter(([, value]) => {
    if (value === null || value === undefined || value === '') {
      return false;
    }
    return !Array.isArray(value) || value.length > 0;
  }),
);

const ARRAY_KEYS = new Set([
  'fetchers', 'locators', 'packageLabels', 'depth', 'sources', 'labels',
  'visibility', 'blockTypes', 'cwes', 'fixTypes', 'severities',
]);

/*
 * `qs.parse` gives a string for `?labels=x` and an array for `?projectName[0]=x`,
 * but `parseQueryParams` assumes each key's shape. Coerce so odd URLs don't crash the page.
 */
const normalizeQuery = (query: qs.ParsedQs): Partial<PackageInventoryQueryParams> => Object.fromEntries(
  Object.entries(query).flatMap(([key, value]): Array<[string, string | string[]]> => {
    if (ARRAY_KEYS.has(key)) {
      const items = Array.isArray(value) ? value.filter(v => typeof v === 'string') : [value];
      return items.every(v => typeof v === 'string') ? [[key, items]] : [];
    }
    const scalar = Array.isArray(value) ? value[0] : value;
    return typeof scalar === 'string' ? [[key, scalar]] : [];
  }),
);

export const parsePackageVersionSearchParams = (search: string): PackageInventoryURLState => {
  const parsed = parseQueryParams(normalizeQuery(qs.parse(search, { ignoreQueryPrefix: true })));
  return {
    page: parsed.page ?? DEFAULT_PAGE,
    count: parsed.count ?? DEFAULT_PAGE_SIZE,
    sort: parsed.sort ?? 'alphabetical',
    filter: parsed.filter ?? {},
  };
};

const usePackageVersionSearchParams = () => {
  const location = useLocation();
  const router = useRouter();
  const teamIds = useSelector(TeamsStore.selectors.getTeamSelection);
  const parsedState = useMemo(
    () => parsePackageVersionSearchParams(location.search ?? ''),
    [location.search],
  );
  const pendingStateRef = useRef(parsedState);

  useEffect(() => {
    pendingStateRef.current = parsedState;
  }, [parsedState]);

  const replaceSearchParams = useCallback((state: PackageInventoryURLState) => {
    router.replace({
      pathname: location.pathname,
      search: `?${qs.stringify(flattenFilters(state))}`,
    });
  }, [location.pathname, router]);

  const setFilters = useCallback((partialFilters: FilterPatch) => {
    const nextState = {
      ...pendingStateRef.current,
      page: DEFAULT_PAGE,
      filter: cleanFilters({
        ...pendingStateRef.current.filter,
        ...partialFilters,
      }),
    };
    pendingStateRef.current = nextState;
    replaceSearchParams(nextState);
  }, [replaceSearchParams]);

  const setPage = useCallback((page: number) => {
    const nextState = { ...pendingStateRef.current, page };
    pendingStateRef.current = nextState;
    replaceSearchParams(nextState);
  }, [replaceSearchParams]);

  const setCount = useCallback((count: number) => {
    const nextState = { ...pendingStateRef.current, page: DEFAULT_PAGE, count };
    pendingStateRef.current = nextState;
    replaceSearchParams(nextState);
  }, [replaceSearchParams]);

  const flattenedState = useMemo(() => flattenFilters(parsedState), [parsedState]);
  const params = useMemo(() => ({ ...flattenedState, teamIds }), [flattenedState, teamIds]);
  const queryString = useMemo(() => qs.stringify(flattenedState), [flattenedState]);
  const unpaginatedQueryString = useMemo(() => qs.stringify(Object.fromEntries(
    Object.entries(flattenedState).filter(([key]) => key !== 'page' && key !== 'count'),
  )), [flattenedState]);

  return {
    filters: parsedState.filter,
    page: parsedState.page,
    count: parsedState.count,
    params,
    queryString,
    unpaginatedQueryString,
    setFilters,
    setPage,
    setCount,
  };
};

export type PackageVersionSearchParams = ReturnType<typeof usePackageVersionSearchParams>;

export default usePackageVersionSearchParams;
